import {CommonModule} from '@angular/common';
import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {UiButtonComponent, UiInputComponent, UiSelectComponent, UiToastService} from 'freyr-ui';
import {AdminApiService} from '../../admin-api.service';
import {AdminDiscordBotSettings, DiscordChannelInfo, DiscordGuild, DiscordLinkedServer} from '../../admin.models';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';
import {AdminI18nService} from '../../core/i18n/admin-i18n.service';
import {AdminPageActionsService} from '../../core/admin-page-actions.service';

@Component({
  selector: 'app-discord-bot-page',
  standalone: true,
  imports: [CommonModule, FormsModule, UiButtonComponent, UiInputComponent, UiSelectComponent, AdminTranslatePipe],
  templateUrl: './discord-bot-page.component.html',
  styleUrl: './discord-bot-page.component.scss'
})
export class DiscordBotPageComponent implements OnInit, OnDestroy {
  private readonly api = inject(AdminApiService);
  private readonly i18n = inject(AdminI18nService);
  private readonly pageActions = inject(AdminPageActionsService);
  private readonly toast = inject(UiToastService);
  private actionsSubscription?: Subscription;

  readonly activityTypes = [
    { value: 'PLAYING', labelKey: 'admin.discordBot.activity.types.PLAYING' },
    { value: 'LISTENING', labelKey: 'admin.discordBot.activity.types.LISTENING' },
    { value: 'WATCHING', labelKey: 'admin.discordBot.activity.types.WATCHING' },
    { value: 'STREAMING', labelKey: 'admin.discordBot.activity.types.STREAMING' },
    { value: 'COMPETING', labelKey: 'admin.discordBot.activity.types.COMPETING' }
  ];

  loading = false;
  saving = false;
  publishingRelease = false;
  linkingDiscord = false;
  loadingDiscordSources = false;
  loadingChannels = false;
  settings: AdminDiscordBotSettings | null = null;
  linkedServers: DiscordLinkedServer[] = [];
  availableGuilds: DiscordGuild[] = [];
  releaseChannels: DiscordChannelInfo[] = [];

  username = '';
  avatarUrl = '';
  applicationDescription = '';
  activityType = 'PLAYING';
  activityText = '';
  releaseAnnouncementsGuildId = '';
  releaseAnnouncementsChannelId = '';

  releaseName = '';
  releaseTagName = '';
  releaseUrl = '';
  releaseDescription = '';
  releasePublishedAt = '';
  releaseCommitSha = '';
  reason = '';

  ngOnInit(): void {
    this.loadSettings();
    this.actionsSubscription = this.pageActions.actions$.subscribe(action => {
      if (action === 'discord-bot-refresh') {
        this.loadSettings();
      }
    });
  }

  ngOnDestroy(): void {
    this.actionsSubscription?.unsubscribe();
  }

  loadSettings(): void {
    this.loading = true;
    this.api.getDiscordBotSettings().subscribe({
      next: settings => {
        this.settings = settings;
        this.username = settings.username ?? '';
        this.avatarUrl = settings.avatarUrl ?? '';
        this.applicationDescription = settings.applicationDescription ?? '';
        this.activityType = settings.activityType ?? 'PLAYING';
        this.activityText = settings.activityText ?? '';
        this.releaseAnnouncementsGuildId = settings.releaseAnnouncementsGuildId ?? '';
        this.releaseAnnouncementsChannelId = settings.releaseAnnouncementsChannelId ?? '';
        this.loading = false;
        this.loadDiscordSources();
      },
      error: error => {
        this.loading = false;
        this.handleError(error, 'admin.discordBot.loadError');
      }
    });
  }

  save(): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.api.updateDiscordBotSettings({
      username: this.username || null,
      avatarUrl: this.avatarUrl || null,
      applicationDescription: this.applicationDescription,
      activityType: this.activityType || null,
      activityText: this.activityText || null,
      releaseAnnouncementsGuildId: this.releaseAnnouncementsGuildId || null,
      releaseAnnouncementsChannelId: this.releaseAnnouncementsChannelId || null,
      reason: this.reason || null
    }).subscribe({
      next: settings => {
        this.settings = settings;
        this.reason = '';
        this.saving = false;
        this.toast.success(this.i18n.t('admin.discordBot.updateSuccess'));
      },
      error: error => {
        this.saving = false;
        this.handleError(error, 'admin.discordBot.updateError');
      }
    });
  }

  resetDraft(): void {
    if (!this.settings) {
      return;
    }
    this.username = this.settings.username ?? '';
    this.avatarUrl = this.settings.avatarUrl ?? '';
    this.applicationDescription = this.settings.applicationDescription ?? '';
    this.activityType = this.settings.activityType ?? 'PLAYING';
    this.activityText = this.settings.activityText ?? '';
    this.releaseAnnouncementsGuildId = this.settings.releaseAnnouncementsGuildId ?? '';
    this.releaseAnnouncementsChannelId = this.settings.releaseAnnouncementsChannelId ?? '';
    this.reason = '';
    this.onReleaseGuildChange(this.releaseAnnouncementsGuildId, false);
  }

  publishRelease(): void {
    if (this.publishingRelease) {
      return;
    }
    this.publishingRelease = true;
    this.api.publishDiscordReleaseAnnouncement({
      channelId: this.releaseAnnouncementsChannelId || null,
      name: this.releaseName || null,
      tagName: this.releaseTagName || null,
      description: this.releaseDescription || null,
      url: this.releaseUrl || null,
      publishedAt: this.releasePublishedAt || null,
      commitSha: this.releaseCommitSha || null,
      reason: this.reason || null
    }).subscribe({
      next: () => {
        this.publishingRelease = false;
        this.toast.success(this.i18n.t('admin.discordBot.release.publishSuccess'));
      },
      error: error => {
        this.publishingRelease = false;
        this.handleError(error, 'admin.discordBot.release.publishError');
      }
    });
  }

  previewActivity(): string {
    const label = this.i18n.t(`admin.discordBot.activity.types.${this.activityType}`);
    return this.activityText.trim() ? `${label} ${this.activityText.trim()}` : label;
  }

  connectDiscord(): void {
    if (this.linkingDiscord) {
      return;
    }
    this.linkingDiscord = true;
    this.api.getDiscordLinkUrl('admin:discord-bot').subscribe({
      next: url => {
        this.linkingDiscord = false;
        if (typeof window !== 'undefined') {
          window.location.href = url;
        }
      },
      error: error => {
        this.linkingDiscord = false;
        this.handleError(error, 'admin.discordBot.release.connectError');
      }
    });
  }

  linkGuild(guild: DiscordGuild): void {
    this.api.linkDiscordServer(guild.id, guild.name).subscribe({
      next: () => {
        this.toast.success(this.i18n.t('admin.discordBot.release.serverLinked'));
        this.loadDiscordSources(guild.id);
      },
      error: error => this.handleError(error, 'admin.discordBot.release.serverLinkError')
    });
  }

  onReleaseGuildChange(guildId: string | null | undefined, resetChannel = true): void {
    this.releaseAnnouncementsGuildId = guildId ?? '';
    if (resetChannel) {
      this.releaseAnnouncementsChannelId = '';
    }
    if (!this.releaseAnnouncementsGuildId) {
      this.releaseChannels = [];
      return;
    }
    this.loadingChannels = true;
    this.api.getDiscordGuildChannels(this.releaseAnnouncementsGuildId).subscribe({
      next: channels => {
        this.releaseChannels = channels
          .filter(channel => channel.type === 0 || channel.type === 5)
          .sort((left, right) => (left.name || '').localeCompare(right.name || ''));
        this.loadingChannels = false;
        if (this.releaseAnnouncementsChannelId
          && !this.releaseChannels.some(channel => channel.id === this.releaseAnnouncementsChannelId)) {
          this.releaseAnnouncementsChannelId = '';
        }
      },
      error: error => {
        this.loadingChannels = false;
        this.releaseChannels = [];
        this.handleError(error, 'admin.discordBot.release.channelsLoadError');
      }
    });
  }

  guildSelectionOptions(): Array<{ value: string; label: string }> {
    return this.linkedServers.map(server => ({
      value: server.guildId,
      label: server.guildName?.trim() || server.guildId
    }));
  }

  channelSelectionOptions(): Array<{ value: string; label: string }> {
    return this.releaseChannels.map(channel => ({
      value: channel.id,
      label: `#${channel.name || channel.id}`
    }));
  }

  selectedGuildLabel(fallbackGuildId?: string | null): string | null {
    const selectedGuildId = this.releaseAnnouncementsGuildId || fallbackGuildId || '';
    return this.guildSelectionOptions().find(option => option.value === selectedGuildId)?.label ?? null;
  }

  selectedChannelLabel(fallbackChannelId?: string | null): string | null {
    const selectedChannelId = this.releaseAnnouncementsChannelId || fallbackChannelId || '';
    return this.channelSelectionOptions().find(option => option.value === selectedChannelId)?.label ?? null;
  }

  unlinkedGuilds(): DiscordGuild[] {
    const linkedIds = new Set(this.linkedServers.map(server => server.guildId));
    return this.availableGuilds.filter(guild => !linkedIds.has(guild.id));
  }

  private loadDiscordSources(preferredGuildId?: string): void {
    this.loadingDiscordSources = true;
    this.api.listDiscordLinkedServers().subscribe({
      next: linkedServers => {
        this.linkedServers = linkedServers;
        this.api.listDiscordGuilds().subscribe({
          next: guilds => {
            this.availableGuilds = guilds;
            this.loadingDiscordSources = false;
            const selectedGuildId = preferredGuildId
              || this.releaseAnnouncementsGuildId
              || this.settings?.releaseAnnouncementsGuildId
              || linkedServers.find(server => server.primary)?.guildId
              || linkedServers[0]?.guildId
              || '';
            if (selectedGuildId) {
              this.onReleaseGuildChange(selectedGuildId, !this.releaseAnnouncementsChannelId);
            } else {
              this.releaseChannels = [];
            }
          },
          error: error => {
            this.loadingDiscordSources = false;
            this.availableGuilds = [];
            this.handleError(error, 'admin.discordBot.release.guildsLoadError');
          }
        });
      },
      error: error => {
        this.loadingDiscordSources = false;
        this.linkedServers = [];
        this.availableGuilds = [];
        this.handleError(error, 'admin.discordBot.release.linkedServersLoadError');
      }
    });
  }

  private handleError(error: HttpErrorResponse, fallbackKey: string): void {
    const message = typeof error.error?.message === 'string' && error.error.message.trim()
      ? error.error.message.trim()
      : this.i18n.t(fallbackKey);
    this.toast.error(message);
  }
}
